import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./index.css";
import "./design/tokens.css"; // Phase-2 design-system tokens (OKLCH ramps + 4 themes as CSS vars)
import App from "./App";
import { initErrorReporter, initWebVitals } from "./lib/analytics";

// Observability (PERF-8 + TECH-5): field Core Web Vitals RUM (lazy web-vitals attribution chunk)
// and the ErrorBoundary `window.__onAppError` reporter. Both are consent-gated inside analytics.js
// (no event leaves the page before the optional-analytics opt-in, which only the public marketing
// surface offers) — registering here just makes the observers early enough to catch the metrics.
initErrorReporter();
initWebVitals();

// i18n robustness: the Google Website Translate widget rewrites the DOM (wrapping text in <font> nodes),
// which makes React's removeChild/insertBefore throw "NotFoundError" and crash the app — most visibly the
// LOGIN flow when it swaps the password step for the 2FA step in a non-English language. These guards
// no-op only the specific "node was already moved by Translate" case, so translation + React coexist.
if (typeof Node === "function" && Node.prototype) {
  const _removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) return child;          // already detached by Translate
    return _removeChild.apply(this, arguments);
  };
  const _insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;   // ref moved by Translate
    return _insertBefore.apply(this, arguments);
  };
}

// SEO prerender (scripts/prerender.mjs) writes real HTML into #root for the public marketing
// routes — hydrate that instead of blowing it away. Non-prerendered pages (the /app SPA shell,
// portals, …) keep an empty #root and take the classic client render.
const rootEl = document.getElementById("root");
if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, <App />, {
    // A recoverable hydration mismatch (e.g. a CMS-fed section whose first client render differs
    // from the snapshot) falls back to client rendering for that boundary — surface it as a
    // warning, not a crash-level console error.
    onRecoverableError: (err) => {
      console.warn("[hydration] recovered:", err && err.message ? err.message : err);
    },
  });
} else {
  createRoot(rootEl).render(<App />);
}

if ("serviceWorker" in navigator) {
  // PERF-13: the app ships NO caching service worker any more — /sw.js stays deployed only as a
  // self-destruct stub for legacy clients still holding the old caching SW (their browser's
  // update-on-navigate check fetches it, it wipes caches and unregisters itself). Production
  // used to RE-register it on every load: a perpetual install→activate→unregister loop plus a
  // wasted /sw.js request per visit. New visits register nothing; instead every environment
  // actively unregisters whatever a previous session left behind and drops its caches, so a
  // reload always loads live code. (InstallPWA relies on `beforeinstallprompt` only — no SW.)
  navigator.serviceWorker.getRegistrations()
    .then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  if (window.caches) {
    caches.keys().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {});
  }
}
